import { api } from "../lib";
import { LEGACY_TECHNICAL_REPORT_MESSAGE } from "../utils/legacyTechnicalReportError";
import type {
  LegacyReportAcknowledgement,
  LegacyReportDraftData,
  LegacyReportCropInput,
  LegacyReportReviewState,
} from "../types/legacyReportReview";

const CONFLICT_MESSAGES = {
  legacy_technical_summary_only: LEGACY_TECHNICAL_REPORT_MESSAGE,
  reconstruction_stale:
    "A newer reconstruction was saved or its source changed. Your edits are retained. Compare the saved draft before trying again.",
  reconstruction_approved:
    "This historical reconstruction has already been approved and cannot be changed. Your local edits are retained for comparison.",
  reconstruction_review_required:
    "Review the remaining source evidence, graphics, and comparison findings, then render the saved revision before approval.",
} as const;

type ReconstructionErrorCode = keyof typeof CONFLICT_MESSAGES;

export class LegacyReportRequestError extends Error {
  readonly status?: number;

  readonly code?: ReconstructionErrorCode;

  constructor(status?: number, code?: ReconstructionErrorCode) {
    super(
      code && (status === 409 || status === 400 || status === 422)
        ? CONFLICT_MESSAGES[code]
        : status === 403
        ? "Your account cannot perform this report review action. Contact an administrator or medical director."
        : status === 404
        ? "This imported report is unavailable. Open the imported case and check its original report."
        : status === 409
        ? "The saved reconstruction changed. Your edits are retained. Compare the saved draft before retrying."
        : status === 400 || status === 422
        ? "The reconstruction could not be accepted. Check the literal fields and source evidence. Your edits are retained."
        : status === 503
        ? "Report rendering or comparison is unavailable. Your saved draft is retained. Try the action again when the service is available."
        : "The request could not be completed. Your edits are retained. Check your connection and try again."
    );
    this.name = "LegacyReportRequestError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    const response = (
      error as {
        response?: { status?: number; data?: { code?: unknown } };
        code?: unknown;
      }
    )?.response;
    const candidate =
      response?.data?.code || (error as { code?: unknown })?.code;
    const code =
      typeof candidate === "string" &&
      Object.prototype.hasOwnProperty.call(CONFLICT_MESSAGES, candidate)
        ? (candidate as ReconstructionErrorCode)
        : undefined;
    throw new LegacyReportRequestError(response?.status, code);
  }
}

const endpoint = (caseId: string) => `/cases/${caseId}/legacy-report`;

export const legacyReportsService = {
  get: (caseId: string) =>
    request(() => api.get<LegacyReportReviewState>(endpoint(caseId))),
  prepare: (caseId: string) =>
    request(() =>
      api.post<LegacyReportReviewState>(
        `${endpoint(caseId)}/prepare`,
        {},
        { timeout: 120000 }
      )
    ),
  crop: (
    caseId: string,
    expectedRevision: number,
    input: LegacyReportCropInput
  ) =>
    request(() =>
      api.post<LegacyReportReviewState>(
        `${endpoint(caseId)}/regions`,
        { expectedRevision, ...input },
        { timeout: 120000 }
      )
    ),
  save: (
    caseId: string,
    expectedRevision: number,
    draft: LegacyReportDraftData
  ) =>
    request(() =>
      api.put<LegacyReportReviewState>(`${endpoint(caseId)}/draft`, {
        expectedRevision,
        draft,
      })
    ),
  preview: (caseId: string, expectedRevision: number) =>
    request(() =>
      api.post<LegacyReportReviewState>(
        `${endpoint(caseId)}/preview`,
        { expectedRevision },
        { timeout: 120000 }
      )
    ),
  compare: (caseId: string, expectedRevision: number) =>
    request(() =>
      api.post<LegacyReportReviewState>(
        `${endpoint(caseId)}/compare`,
        { expectedRevision },
        { timeout: 240000 }
      )
    ),
  acknowledge: (caseId: string, payload: LegacyReportAcknowledgement) =>
    request(() =>
      api.post<LegacyReportReviewState & { acknowledgementId: string }>(
        `${endpoint(caseId)}/acknowledge`,
        payload
      )
    ),
  approve: (
    caseId: string,
    expectedRevision: number,
    acknowledgementId: string
  ) =>
    request(() =>
      api.post<LegacyReportReviewState>(`${endpoint(caseId)}/approve`, {
        expectedRevision,
        acknowledgementId,
      })
    ),
};
